import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Search, Plus, Trash2, Edit3, Sparkles, AlertCircle, X, Save, Loader2, Calendar, Building2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DocumentScannerBox from "../../components/DocumentScannerBox";

const COMPAGNIE = ["Unipol", "Generali", "Allianz", "AXA", "Reale Mutua", "Zurich", "Groupama", "ITAS", "Vittoria Assicurazioni", "Cattolica", "Linear", "Genertel", "Direct Line", "Quixa", "Verti"];

const empty = () => ({
  targa: "", compagnia: "Unipol", numero_polizza: "", broker: "",
  data_emissione: "", data_scadenza: "",
  premio_annuo: 0, franchigia_rca: 500, franchigia_kasko: 1000,
  franchigia_furto: 500, franchigia_incendio: 500, massimale_rca: 6000000,
  atti_vandalici: false, cristalli: false, assistenza_stradale: true, infortuni_conducente: false,
  note: "",
});

export default function Assicurazioni() {
  const [polizze, setPolizze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const [lookupTarga, setLookupTarga] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/polizze");
      setPolizze(r.data || []);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = (search || "").toLowerCase().trim();
    if (!s) return polizze;
    return polizze.filter((p) =>
      `${p.targa} ${p.compagnia} ${p.numero_polizza} ${p.marca || ""} ${p.modello || ""}`.toLowerCase().includes(s)
    );
  }, [polizze, search]);

  const kpi = useMemo(() => ({
    tot: polizze.length,
    scadute: polizze.filter((p) => p.scaduta).length,
    inScadenza: polizze.filter((p) => p.in_scadenza).length,
    premioTot: polizze.reduce((a, p) => a + (+p.premio_annuo || 0), 0),
  }), [polizze]);

  const openNew = () => { setForm(empty()); setEditId(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...empty(), ...p }); setEditId(p.id); setShowForm(true); };

  const doLookup = async () => {
    const t = (lookupTarga || form.targa || "").toUpperCase().trim();
    if (!t) { toast.error("Inserisci una targa"); return; }
    setLookupBusy(true);
    try {
      const r = await api.get(`/polizze/lookup-by-targa/${encodeURIComponent(t)}`);
      const d = r.data?.data || {};
      setForm((f) => ({
        ...f,
        targa: t,
        compagnia: d.compagnia || f.compagnia,
        numero_polizza: d.numero_polizza || f.numero_polizza,
        data_scadenza: d.data_scadenza || f.data_scadenza,
        premio_annuo: d.premio_annuo || f.premio_annuo,
        franchigia_rca: d.franchigia_rca ?? f.franchigia_rca,
        franchigia_kasko: d.franchigia_kasko ?? f.franchigia_kasko,
        massimale_rca: d.massimale_rca ?? f.massimale_rca,
        note: d.note || f.note,
      }));
      const src = r.data?.source;
      const labels = { database: "✅ Trovata nel database", vehicle_legacy: "📄 Trovata dati legacy veicolo", ai_fallback: "🤖 Stima AI generata" };
      toast.success(labels[src] || "Lookup completato");
      if (!showForm) setShowForm(true);
    } catch (e) {
      toast.error("Errore lookup: " + (e?.response?.data?.detail || e.message));
    } finally { setLookupBusy(false); }
  };

  const save = async () => {
    if (!form.targa) { toast.error("Targa obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = { ...form, premio_annuo: +form.premio_annuo || 0 };
      if (editId) {
        await api.put(`/polizze/${editId}`, payload);
        toast.success("Polizza aggiornata");
      } else {
        await api.post("/polizze", payload);
        toast.success("Polizza creata");
      }
      setShowForm(false); setEditId(null);
      load();
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare la polizza?")) return;
    await api.delete(`/polizze/${id}`);
    toast.success("Eliminata");
    load();
  };

  return (
    <div className="space-y-6" data-testid="assicurazioni-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <ShieldCheck className="text-[#ff0033]" size={26} />
            Assicurazioni
          </h1>
          <p className="text-sm text-white/45 mt-1">
            Polizze veicoli — gestione scadenze, premi, franchigie. Lookup automatico per targa con AI fallback.
          </p>
        </div>
        <button onClick={openNew} data-testid="new-polizza-btn"
          className="btn-neon px-5 py-2.5 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuova polizza
        </button>
      </div>

      {/* QUICK LOOKUP */}
      <div className="glass rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-widest text-white/40">Lookup polizza per targa</label>
          <input value={lookupTarga} onChange={(e) => setLookupTarga(e.target.value.toUpperCase())}
            placeholder="EJ754PD" data-testid="lookup-targa-input"
            className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:border-[#ff0033]/60 focus:outline-none" />
        </div>
        <button onClick={doLookup} disabled={lookupBusy} data-testid="lookup-targa-btn"
          className="btn-neon px-5 py-2 rounded-lg text-xs inline-flex items-center gap-2 disabled:opacity-50">
          {lookupBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          Cerca / Stima AI
        </button>
        <div className="text-[10px] text-white/40 w-full">
          💡 Se la polizza esiste viene caricata. Altrimenti, l'AI genera una stima (compagnia, premio, franchigie) sempre modificabile.
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Totale Polizze" value={kpi.tot} accent="white" icon={ShieldCheck} />
        <KPI label="Scadute" value={kpi.scadute} accent="rose" icon={AlertCircle} />
        <KPI label="In scadenza (30gg)" value={kpi.inScadenza} accent="amber" icon={Calendar} />
        <KPI label="Premio Annuo Tot." value={`€ ${kpi.premioTot.toLocaleString("it-IT")}`} accent="emerald" icon={Building2} />
      </div>

      {/* SEARCH */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca targa, compagnia, n° polizza…" data-testid="polizze-search"
          className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm" />
      </div>

      {/* TABLE */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-xs" data-testid="polizze-table">
            <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Targa</th>
                <th className="px-2 py-2 text-left">Veicolo</th>
                <th className="px-2 py-2 text-left">Compagnia</th>
                <th className="px-2 py-2 text-left">N° Polizza</th>
                <th className="px-2 py-2 text-right">Premio €</th>
                <th className="px-2 py-2 text-left">Scadenza</th>
                <th className="px-2 py-2 text-center">Stato</th>
                <th className="px-2 py-2 text-center">Coperture</th>
                <th className="px-2 py-2 text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-10 text-white/40">Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12">
                  <div className="text-white/40 mb-3">Nessuna polizza registrata</div>
                  <button onClick={openNew} className="btn-neon px-4 py-2 rounded-full text-xs">+ Aggiungi prima polizza</button>
                </td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-2 font-mono font-bold text-white">{p.targa}</td>
                  <td className="px-2 py-2 text-white/80">{p.marca} {p.modello || <span className="text-white/30 italic">—</span>}</td>
                  <td className="px-2 py-2 text-white/80">{p.compagnia}</td>
                  <td className="px-2 py-2 font-mono text-white/70">{p.numero_polizza || "—"}</td>
                  <td className="px-2 py-2 text-right font-mono">{p.premio_annuo ? `€ ${(+p.premio_annuo).toLocaleString("it-IT")}` : "—"}</td>
                  <td className="px-2 py-2">{p.data_scadenza || "—"}</td>
                  <td className="px-2 py-2 text-center">
                    {p.scaduta ? <Badge tone="rose">SCADUTA ({Math.abs(p.giorni_a_scadenza)}gg fa)</Badge>
                      : p.in_scadenza ? <Badge tone="amber">{p.giorni_a_scadenza}gg</Badge>
                      : p.giorni_a_scadenza != null ? <Badge tone="emerald">OK ({p.giorni_a_scadenza}gg)</Badge>
                      : <Badge tone="zinc">—</Badge>}
                  </td>
                  <td className="px-2 py-2 text-center text-[10px] text-white/55">
                    {[p.atti_vandalici && "AV", p.cristalli && "CR", p.assistenza_stradale && "AS", p.infortuni_conducente && "IC"].filter(Boolean).join(" · ") || "RCA"}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-white/10 text-white/70" data-testid={`pol-edit-${p.targa}`}><Edit3 size={13} /></button>
                      <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-rose-500/20 text-rose-300" data-testid={`pol-del-${p.targa}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL FORM */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="polizza-form-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold">{editId ? "Modifica polizza" : "Nuova polizza"}</h2>
                <p className="text-xs text-white/45 mt-1">Compila i dati. La targa attiva il collegamento al veicolo.</p>
              </div>
              <button onClick={() => setShowForm(false)} className="text-white/40"><X size={20} /></button>
            </div>

            {/* AI Scanner Polizza */}
            <DocumentScannerBox
              testIdPrefix="ass-scanner"
              context="fleet"
              variant="full"
              title="Scansione AI · Polizza assicurativa"
              description="Carica polizza/certificato assicurativo: l'AI estrae targa, compagnia, numero polizza e scadenze."
              onResult={(d) => {
                const v = d?.veicolo || {};
                const doc = d?.documento || {};
                setForm((f) => ({
                  ...f,
                  targa: (v.targa || f.targa || "").toUpperCase(),
                  compagnia: doc.ente_emissione || f.compagnia,
                  numero_polizza: doc.numero || f.numero_polizza,
                  data_emissione: doc.data_emissione || f.data_emissione,
                  data_scadenza: doc.data_scadenza || f.data_scadenza,
                }));
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Targa *">
                <div className="flex gap-2">
                  <input value={form.targa} onChange={(e) => setForm({ ...form, targa: e.target.value.toUpperCase() })}
                    placeholder="EJ754PD" data-testid="form-targa"
                    className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono uppercase" />
                  <button onClick={doLookup} disabled={!form.targa || lookupBusy} title="Cerca / Stima AI"
                    className="px-3 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40">
                    {lookupBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  </button>
                </div>
              </Field>
              <Field label="Compagnia">
                <input list="compagnie-list" value={form.compagnia} onChange={(e) => setForm({ ...form, compagnia: e.target.value })}
                  data-testid="form-compagnia" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                <datalist id="compagnie-list">
                  {COMPAGNIE.map((c) => <option key={c} value={c} />)}
                </datalist>
              </Field>
              <Field label="N° Polizza">
                <input value={form.numero_polizza} onChange={(e) => setForm({ ...form, numero_polizza: e.target.value })}
                  data-testid="form-num-polizza" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Broker / Agenzia">
                <input value={form.broker || ""} onChange={(e) => setForm({ ...form, broker: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <Field label="Data emissione">
                <input type="date" value={form.data_emissione || ""} onChange={(e) => setForm({ ...form, data_emissione: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <Field label="Data scadenza *">
                <input type="date" value={form.data_scadenza || ""} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })}
                  data-testid="form-data-scadenza" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <Field label="Premio annuo (€)">
                <input type="number" value={form.premio_annuo || 0} onChange={(e) => setForm({ ...form, premio_annuo: +e.target.value })}
                  data-testid="form-premio" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Massimale RCA (€)">
                <input type="number" value={form.massimale_rca || 0} onChange={(e) => setForm({ ...form, massimale_rca: +e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Franchigia RCA (€)">
                <input type="number" value={form.franchigia_rca || 0} onChange={(e) => setForm({ ...form, franchigia_rca: +e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Franchigia Kasko (€)">
                <input type="number" value={form.franchigia_kasko || 0} onChange={(e) => setForm({ ...form, franchigia_kasko: +e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Franchigia Furto (€)">
                <input type="number" value={form.franchigia_furto || 0} onChange={(e) => setForm({ ...form, franchigia_furto: +e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Franchigia Incendio (€)">
                <input type="number" value={form.franchigia_incendio || 0} onChange={(e) => setForm({ ...form, franchigia_incendio: +e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </Field>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Coperture accessorie</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["atti_vandalici", "Atti vandalici"],
                  ["cristalli", "Cristalli"],
                  ["assistenza_stradale", "Assistenza stradale"],
                  ["infortuni_conducente", "Infortuni conducente"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/[0.05]">
                    <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} />
                    <span className="text-xs">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Field label="Note" className="mt-3">
              <textarea value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </Field>

            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="form-save-btn"
                className="btn-neon px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-40">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editId ? "Aggiorna" : "Crea"} polizza
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[10px] uppercase tracking-widest text-white/40">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Badge = ({ tone, children }) => {
  const tones = {
    rose: "bg-rose-500/20 text-rose-300 border-rose-500/40",
    amber: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    emerald: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    zinc: "bg-white/[0.04] text-white/40 border-white/10",
  };
  return <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${tones[tone] || tones.zinc}`}>{children}</span>;
};

const KPI = ({ label, value, accent, icon: Icon }) => {
  const tones = {
    white: "text-white",
    rose: "text-rose-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
  };
  return (
    <div className="glass rounded-xl p-4 flex items-center gap-3">
      <Icon size={20} className={tones[accent] || "text-white/60"} />
      <div>
        <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
        <div className={`font-display font-black text-xl ${tones[accent] || "text-white"} mt-0.5`}>{value}</div>
      </div>
    </div>
  );
};
